import React, { useContext, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'
import track from '../../lib/track'
import useEntryMotion from '../motion/useEntryMotion'
import { splitLocale } from '../../config/i18nRoutes'
import './ConversionBar.scss'

const HIDE_ON = ['/contact-us']
const DISMISS_KEY = 'mlmind:bar-dismissed'

/**
 * A slim action bar that slides in once the visitor has read a good part of
 * the page. Two actions only: the estimator and a call. On the estimator
 * itself only the call remains, and the contact page does without it.
 * Dismissing it lasts for the session.
 */
const ConversionBar = () => {
  const { translations: t } = useContext(LanguageContext)
  const { pathname: fullPath } = useLocation()
  const pathname = splitLocale(fullPath).base
  const navigate = useNavigate()
  const animateIn = useEntryMotion()
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === '1'
    } catch (e) {
      return false
    }
  })

  useEffect(() => {
    if (dismissed || HIDE_ON.includes(pathname)) return undefined
    const onScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      const ratio = scrollable > 0 ? window.scrollY / scrollable : 0
      setVisible(ratio > 0.35 && ratio < 0.97)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [pathname, dismissed])

  const dismiss = () => {
    setDismissed(true)
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1')
    } catch (e) {
      // session storage blocked — hide for this page only
    }
  }

  const onEstimate = () => {
    track('estimator_click', { from: 'bar', path: pathname })
    navigate('/estimate')
  }
  const onCall = () => {
    track('call_click', { from: 'bar', path: pathname })
    navigate('/contact-us#book')
  }

  const show = visible && !dismissed && !HIDE_ON.includes(pathname)
  const onEstimator = pathname === '/estimate'
  const barRef = useRef(null)

  // While the bar is up and reaches into the bottom-right corner, publish
  // how much room it takes, so the back-to-top button can sit above it.
  useEffect(() => {
    const root = document.documentElement
    if (!show) {
      root.style.setProperty('--cbar-offset', '0px')
      return undefined
    }
    const measure = () => {
      const bar = barRef.current
      if (!bar) return
      // Height and CSS bottom rather than the on-screen box: the slide-in
      // animation moves the box, but not these.
      const rect = bar.getBoundingClientRect()
      const overlapsCorner = rect.right > window.innerWidth - 90
      const room = bar.offsetHeight + (parseFloat(getComputedStyle(bar).bottom) || 0)
      root.style.setProperty('--cbar-offset', overlapsCorner ? `${Math.round(room)}px` : '0px')
    }
    const frame = requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', measure)
      root.style.setProperty('--cbar-offset', '0px')
    }
  }, [show, onEstimator])

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          ref={barRef}
          className='cbar'
          role='complementary'
          aria-label={t.barLabel}
          initial={animateIn ? { y: 80, opacity: 0 } : false}
          animate={{ y: 0, opacity: 1 }}
          exit={animateIn ? { y: 80, opacity: 0 } : undefined}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className='cbar-text'>{onEstimator ? t.barTextEstimator : t.barText}</p>
          <div className='cbar-actions'>
            {!onEstimator && (
              <button type='button' className='cbar-btn' onClick={onEstimate}>
                {t.barEstimate} <span aria-hidden='true'>→</span>
              </button>
            )}
            <button type='button' className={`cbar-btn ${onEstimator ? '' : 'cbar-btn-ghost'}`} onClick={onCall}>
              {t.barCall}
            </button>
          </div>
          <button type='button' className='cbar-close' onClick={dismiss} aria-label={t.barClose}>×</button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ConversionBar
